import {useLegacyGrpcRequestCallback} from '@aboutyou/data-fetcher';
/* eslint-disable monorepo/no-internal-import */
import {
  UpdateCustomerRequest,
  UpdateCustomerResponse,
} from '@aboutyou/grpc-clients/grips/protos/checkout/checkout_v1.types';
import {CustomerGender} from '@aboutyou/grpc-clients/grips/protos/shared/shared.types';

import {CheckoutV1_UpdateCustomerLazy} from '@aboutyou/grpc-clients/grips/protos/checkout/checkout_v1.grpc.lazy';
import {
  OptionalUserState,
  useOptionalUser,
  UserGenders,
  useUser,
  useUserGender,
  useUserToken,
} from '@aboutyou/user';
import {useCallback} from 'react';
import {MarkOptional} from 'ts-essentials';
import {useSavedUser} from '../../Authentication/AuthenticationModal/hooks/useSavedUser';

export const useUpdateCustomerData = () => {
  const authToken = useUserToken() ?? '';
  const {data: currentUserData, updateCache: updateUser} = useUser();
  const {saveUser} = useSavedUser();
  const requestCallback = useLegacyGrpcRequestCallback(
    CheckoutV1_UpdateCustomerLazy,
  );
  const userGender = useUserGender();
  const {updateCache: updateOptionalUserCache} = useOptionalUser();

  const updateUserPersonalData = useCallback(
    (
      {
        birthDate,
        email,
        firstName,
        lastName,
        password,
        phone,
        gender,
      }: MarkOptional<
        Pick<
          UpdateCustomerRequest,
          | 'birthDate'
          | 'email'
          | 'firstName'
          | 'lastName'
          | 'password'
          | 'phone'
          | 'gender'
        >,
        'gender'
      >,
      {
        onSuccess,
        onError,
        onFinally,
      }: {
        onSuccess: VoidFunction;
        onError: ({code}: {code: number}) => void;
        onFinally?: VoidFunction;
      },
    ) => {
      requestCallback
        .mutateAsync({
          password: password ?? null,
          firstName: firstName ?? null,
          lastName: lastName ?? null,
          phone: phone ?? null,
          birthDate: birthDate ?? null,
          email: email ?? null,
          gender: gender ?? getCustomerGender(userGender),
          session: {
            authToken,
          },
        })
        .then(({customer: updatedCustomer}: UpdateCustomerResponse) => {
          saveUser({
            name: updatedCustomer.firstName,
            email: updatedCustomer.email,
            loginType: 'email',
          });

          const optionalUserState: OptionalUserState = {
            userData: {
              id: updatedCustomer.id,
              email: updatedCustomer.email,
              emailHash: updatedCustomer.emailHashes,
              firstName: updatedCustomer.firstName,
              lastName: updatedCustomer.lastName,
              isExistingCustomer: !updatedCustomer.isNewCustomer,
              gender: getUserGender(updatedCustomer.gender),
              birthday: updatedCustomer.birthday.toISOString(),
              phone: updatedCustomer.phone,
            },
          };

          updateOptionalUserCache(optionalUserState);
          updateUser({
            ...currentUserData,
          });
        })
        .then(onSuccess)
        .catch(onError)
        .finally(onFinally);
    },
    [currentUserData],
  );

  return updateUserPersonalData;
};

const getCustomerGender = (gender?: UserGenders) => {
  return gender === 'male' ? CustomerGender.MALE : CustomerGender.FEMALE;
};

const getUserGender = (gender?: CustomerGender) => {
  return gender === CustomerGender.MALE ? UserGenders.Male : UserGenders.Female;
};
